import React, { useState, useRef, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import { stores } from "../../../stores";
import {
  BannerContainer,
  BannerSlider,
  BannerSlide,
  BannerContent,
  BannerImage,
  BannerPagination,
} from "../Styled/HomeStyled";
import { Typography, Box, Stack } from "@mui/material";
import { callBrowser } from "@/nativebridge/JsToNative";
import { COMMON_UTIL } from "../../../common/util/common.util";

const HomeBanner = observer(() => {
  const { languageStore } = stores;
  const { languageData } = languageStore;

  // 배너 데이터 - 언어에 따라 동적으로 변경
  const BANNER_DATA = [
    {
      id: 1,
      subtitle: languageData.banner2_subtitle,
      subtitleBgColor: "#91BDA2",
      subtitleTextColor: "#333",
      title: languageData.banner2_title,
      buttonText: languageData.banner2_buttonText,
      buttonTextColor: "#333",
      backgroundColor: "#E9F2EC",
      backgroundImage: "",
      image: "/images/banner01_ico.svg",
      url: "https://mkt.shopping.naver.com/link/6a268f7324da0713bf46fb0e"
    },
    {
      id: 2,
      subtitle: languageData.banner3_subtitle,
      subtitleBgColor: "#F5C208",
      subtitleTextColor: "#333",
      title: languageData.banner3_title,
      buttonText: languageData.banner3_buttonText,
      buttonTextColor: "#333",
      backgroundColor: "#FDF3CE",
      backgroundImage: "",
      image: "/images/banner02_ico.svg",
      url: "https://mkt.shopping.naver.com/link/6a268f731e63133d2865db44"
    },
    {
      id: 3,
      subtitle: languageData.banner4_subtitle,
      subtitleBgColor: "#D20B17",
      subtitleTextColor: "#fff",
      title: languageData.banner4_title,
      buttonText: languageData.banner4_buttonText,
      buttonTextColor: "#333",
      backgroundColor: "#F6CED1",
      backgroundImage: "",
      image: "/images/banner03_ico.svg",
      url: "https://mkt.shopping.naver.com/link/6a268f73462cc0227819394d"
    },
    {
      id: 4,
      subtitle: languageData.banner5_subtitle,
      subtitleBgColor: "#019A4D",
      subtitleTextColor: "#fff",
      title: languageData.banner5_title,
      buttonText: languageData.banner5_buttonText,
      buttonTextColor: "#333",
      backgroundColor: "#CCEBDB",
      backgroundImage: "",
      image: "/images/banner04_ico.svg",
      url: "https://mkt.shopping.naver.com/link/6a268f73c61af4258ec028d5"
    },
    {
      id: 5,
      subtitle: languageData.banner6_subtitle,
      subtitleBgColor: "#0189DC",
      subtitleTextColor: "#fff",
      title: languageData.banner6_title,
      buttonText: languageData.banner6_buttonText,
      buttonTextColor: "#333",
      backgroundColor: "#CCE7F8",
      backgroundImage: "",
      image: "/images/banner05_ico.svg",
      url: "https://mkt.shopping.naver.com/link/6a268f73462cc0227819394e"
    },
  ];

  // 배너 데이터 구성
  const isSingleBanner = BANNER_DATA.length === 1;

  // 무한 루프를 위한 확장된 배너 데이터 (단일 배너가 아닐 때만 클론 생성)
  const bannerData = isSingleBanner
    ? [...BANNER_DATA]
    : [
      { ...BANNER_DATA[BANNER_DATA.length - 1], id: 'clone-last' },
      ...BANNER_DATA,
      { ...BANNER_DATA[0], id: 'clone-first' }
    ];

  // 슬라이더 상태 (단일 배너면 0번 인덱스부터 시작)
  const [currentIndex, setCurrentIndex] = useState(isSingleBanner ? 0 : 1);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [hasSwiped, setHasSwiped] = useState(false);

  // refs
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const clickedBannerRef = useRef(null);
  const isAdjustingRef = useRef(false);

  // 자동 슬라이드 관련 함수들
  const startAutoSlide = () => {
    if (isSingleBanner) return; // 단일 배너면 시작 안 함
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      // 인덱스 조정 중이면 스킵
      if (isAdjustingRef.current) return;

      setCurrentIndex(prev => {
        // 조정 중이면 변경하지 않음
        if (isAdjustingRef.current) return prev;

        const nextIndex = prev + 1;
        // 범위 체크
        if (nextIndex >= bannerData.length - 1) {
          return nextIndex; // 인덱스 조정 로직에서 처리
        }
        return nextIndex;
      });
    }, 5000);
  };

  const stopAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const restartAutoSlide = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(startAutoSlide, 3000);
  };

  // 스와이프 핸들러들 (간소화)
  const handleSwipeStart = (clientX, banner) => {
    stopAutoSlide();
    startX.current = clientX;
    currentX.current = clientX;
    clickedBannerRef.current = banner;
    setIsDragging(true);
    setHasSwiped(false);
  };

  const handleSwipeMove = (clientX) => {
    if (!isDragging) return;
    currentX.current = clientX;
  };

  const handleSwipeEnd = () => {
    if (!isDragging || isAdjustingRef.current) return;

    const distance = startX.current - currentX.current;
    const absDistance = Math.abs(distance);
    const isLeftSwipe = distance > 20;
    const isRightSwipe = distance < -20;

    // 움직임이 거의 없으면 클릭으로 간주 (5px 이하)
    const isClick = absDistance < 5 && clickedBannerRef.current;

    if (isClick && clickedBannerRef.current?.url) {
      // 클릭으로 간주되고 URL이 있으면 링크 열기
      if (COMMON_UTIL.isWebView()) {
        callBrowser(clickedBannerRef.current.url);
      } else {
        window.open(clickedBannerRef.current.url, '_blank', 'noopener,noreferrer');
      }
      setIsDragging(false);
      clickedBannerRef.current = null;
      restartAutoSlide();
      return;
    }

    if (isLeftSwipe && !isSingleBanner) {
      setCurrentIndex(prev => {
        const nextIndex = prev + 1;
        // 범위 체크 - 최대값을 넘어가면 조정 로직에서 처리
        return nextIndex >= bannerData.length - 1 ? nextIndex : nextIndex;
      });
    } else if (isRightSwipe && !isSingleBanner) {
      setCurrentIndex(prev => {
        const prevIndex = prev - 1;
        // 범위 체크 - 최소값을 넘어가면 조정 로직에서 처리
        return prevIndex <= 0 ? prevIndex : prevIndex;
      });
    }

    setIsDragging(false);
    setHasSwiped(isLeftSwipe || isRightSwipe);
    clickedBannerRef.current = null;
    restartAutoSlide();
  };

  // 이벤트 핸들러들 (간소화)
  const handleTouchStart = (e, banner) => {
    e.preventDefault();
    handleSwipeStart(e.touches[0].clientX, banner);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    handleSwipeMove(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleSwipeEnd();
  };

  const handleMouseDown = (e, banner) => {
    // 웹에서는 preventDefault를 하지 않아서 클릭 이벤트가 정상 작동하도록 함
    handleSwipeStart(e.clientX, banner);
  };

  // 무한 루프를 위한 인덱스 조정
  useEffect(() => {
    if (isSingleBanner) return; // 단일 배너면 루프 로직 필요 없음

    let timeoutId;

    // 인덱스 조정이 필요한 경우
    if (currentIndex === 0 || currentIndex === bannerData.length - 1) {
      isAdjustingRef.current = true;

      if (currentIndex === 0) {
        timeoutId = setTimeout(() => {
          setIsTransitioning(false);
          setCurrentIndex(BANNER_DATA.length);
          setTimeout(() => {
            setIsTransitioning(true);
            isAdjustingRef.current = false;
          }, 10);
        }, 400);
      } else if (currentIndex === bannerData.length - 1) {
        timeoutId = setTimeout(() => {
          setIsTransitioning(false);
          setCurrentIndex(1);
          setTimeout(() => {
            setIsTransitioning(true);
            isAdjustingRef.current = false;
          }, 10);
        }, 400);
      }
    } else {
      // 정상 범위 내이면 조정 플래그 해제
      isAdjustingRef.current = false;
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentIndex, bannerData.length, BANNER_DATA.length]);

  // 컴포넌트 마운트 시 자동 슬라이드 시작
  useEffect(() => {
    startAutoSlide();
    return () => {
      stopAutoSlide();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // 마우스 드래그를 위한 전역 리스너
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        e.preventDefault(); // 드래그 중에만 preventDefault
        handleSwipeMove(e.clientX);
      }
    };

    const handleMouseUp = (e) => {
      if (isDragging) {
        e.preventDefault(); // 드래그 종료 시에만 preventDefault
        handleSwipeEnd();
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // hasSwiped 상태 리셋
  useEffect(() => {
    if (hasSwiped) {
      const timer = setTimeout(() => setHasSwiped(false), 100);
      return () => clearTimeout(timer);
    }
  }, [hasSwiped]);

  // 배너 클릭 핸들러
  const handleBannerClick = (banner) => {
    if (isDragging || hasSwiped) return;

    // URL이 있으면 외부 링크로 이동
    if (banner.url) {
      if (COMMON_UTIL.isWebView()) {
        callBrowser(banner.url);
      } else {
        window.open(banner.url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  // 변환값 계산
  const getTransformValue = () => {
    // 범위를 벗어난 인덱스 처리
    let safeIndex = currentIndex;
    if (currentIndex < 0) safeIndex = 0;
    if (currentIndex >= bannerData.length) safeIndex = bannerData.length - 1;

    if (isDragging && startX.current !== 0 && currentX.current !== 0) {
      if (isSingleBanner) return 0; // 단일 배너면 드래그 중에도 이동 차단

      const dragDistance = currentX.current - startX.current;
      const baseTransform = -safeIndex * 100;
      const dragPercentage = (dragDistance / window.innerWidth) * 100;
      return baseTransform + dragPercentage;
    }
    return -safeIndex * 100;
  };

  // 현재 페이지 번호 계산
  const getCurrentPage = () => {
    if (isSingleBanner) return 1;

    // 범위를 벗어난 경우 처리
    if (currentIndex === 0) return BANNER_DATA.length;
    if (currentIndex === bannerData.length - 1) return 1;

    // 범위 내의 경우 (1 ~ BANNER_DATA.length)
    if (currentIndex >= 1 && currentIndex <= BANNER_DATA.length) {
      return currentIndex;
    }

    // 예상치 못한 경우 범위로 제한
    if (currentIndex < 1) return BANNER_DATA.length;
    if (currentIndex > BANNER_DATA.length) return 1;

    return currentIndex;
  };

  return (
    <Box pt={4} sx={{ overflow: 'hidden' }}>
      <BannerContainer>
        <BannerSlider
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            transform: `translateX(${getTransformValue()}%)`,
            transition: (isDragging || !isTransitioning) ? 'none' : 'transform 0.4s ease-out',
            userSelect: 'none',
            touchAction: 'none',
          }}
        >
          {bannerData.map((banner, index) => {
            const isCurrentSlide = index === currentIndex;
            const isCloneSlide = !isSingleBanner && (index === 0 || index === bannerData.length - 1);
            const shouldShowImageAnimation = isSingleBanner ? true : (isCurrentSlide && !isCloneSlide);

            return (
              <BannerSlide
                key={`${banner.id}-${index}`}
                style={{
                  backgroundColor: banner.backgroundColor,
                  backgroundImage: banner.backgroundImage ? `url(${banner.backgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'left',
                  backgroundRepeat: 'no-repeat',
                  cursor: banner.url ? 'pointer' : 'default',
                }}
                onMouseDown={(e) => handleMouseDown(e, banner)}
                onTouchStart={(e) => handleTouchStart(e, banner)}
                onClick={banner.url ? () => handleBannerClick(banner) : undefined}
              >
                <BannerContent>
                  {banner.subtitle && (
                    <Box
                      style={{
                        padding: '0 8px',
                        background: banner.subtitleBgColor,
                        borderRadius: '100px',
                        display: 'inline-block',
                        marginBottom: 4,
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          color: banner.subtitleTextColor,
                        }}
                      >
                        {banner.subtitle}
                      </Typography>
                    </Box>
                  )}
                  {banner.title && (
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      lineHeight='1.2!important'
                      style={{
                        color: banner.textColor,
                        letterSpacing: '-0.5px',
                      }}
                    >
                      {banner.title}
                    </Typography>
                  )}
                  {banner.buttonText && (
                    <Stack flexDirection="row" alignItems="center" gap={0.5} mt={0.5}>
                      <Typography
                        variant="caption"
                        style={{
                          color: banner.buttonTextColor,
                        }}
                      >
                        {banner.buttonText}
                      </Typography>
                    </Stack>
                  )}
                </BannerContent>
                {banner.image && (
                  <BannerImage
                    key={`banner-image-${banner.id}-${index}`}
                    src={banner.image}
                    alt={banner.title || 'Banner'}
                    isVisible={shouldShowImageAnimation}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </BannerSlide>
            );
          })}
        </BannerSlider>

        {!isSingleBanner && (
          <BannerPagination>
            <Typography variant="caption" >
              {getCurrentPage()}/{BANNER_DATA.length}
            </Typography>
          </BannerPagination>
        )}
      </BannerContainer>
    </Box>
  );
});

export default HomeBanner;
